import React, { useState, useRef, useEffect, useCallback } from 'react';
import { characters } from './data/characters';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { supabase } from './lib/supabase';
import { createWeightedDeck, charactersWithIndex } from './utils/deckUtils';
import { useSound } from './hooks/useSound';
import { useAuth } from './hooks/useAuth';
import Paywall from './components/Paywall';
import FreeLimitModal from './components/FreeLimitModal';
import StreakCelebration from './components/StreakCelebration';
import AuthForm from './components/AuthForm';
import FlashCard from './components/FlashCard';

const AppContent = () => {
  const { isPro, loading: subscriptionLoading } = useSubscription();

  // Character lists state (persisted)
  const [knownChars, setKnownChars] = useState(new Set());
  const [learningChars, setLearningChars] = useState(new Set());

  // Callback for when user progress is loaded
  const handleUserProgressLoad = useCallback((known, learning) => {
    setKnownChars(known);
    setLearningChars(learning);
    setStats(s => ({ ...s, known: known.size, learning: learning.size }));
  }, []);

  // Auth hook
  const {
    user,
    guestMode,
    setGuestMode,
    authLoading,
    authMode,
    setAuthMode,
    authError,
    email,
    setEmail,
    password,
    setPassword,
    handleSignUp,
    handleSignIn,
    handleSignInWithGoogle,
    handleSignOut: authSignOut,
    continueAsGuest,
  } = useAuth(handleUserProgressLoad);

  const [showPaywall, setShowPaywall] = useState(false);
  const [showFreeLimitModal, setShowFreeLimitModal] = useState(false);

  // Show free limit modal for non-pro users on mount (once per session)
  // Also ensures logic runs when auth/subscription loading settles
  useEffect(() => {
    if (!authLoading && !subscriptionLoading) {
      const hasSeenModal = sessionStorage.getItem('seenFreeLimitModal');
      // If guest or logged in user, and NOT pro
      const isFreeUser = (guestMode || user) && !isPro;

      if (isFreeUser && !hasSeenModal) {
        const timer = setTimeout(() => {
          setShowFreeLimitModal(true);
          sessionStorage.setItem('seenFreeLimitModal', 'true');
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [authLoading, user, guestMode, isPro, subscriptionLoading]);

  // Reset scroll position when auth state changes or component mounts
  // This fixes the issue where the keyboard pushes the view up and it gets stuck there
  useEffect(() => {
    window.scrollTo(0, 0);
    // Add timeouts to ensure scroll resets after keyboard animation finishes
    const t1 = setTimeout(() => window.scrollTo(0, 0), 100);
    const t2 = setTimeout(() => window.scrollTo(0, 0), 300);
    const t3 = setTimeout(() => window.scrollTo(0, 0), 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [user, guestMode]);

  // Flashcard state
  // Re-initialize deck when isPro changes
  const [deck, setDeck] = useState(() => createWeightedDeck(charactersWithIndex, isPro));

  // Effect to update deck when isPro status changes
  useEffect(() => {
    setDeck(() => createWeightedDeck(charactersWithIndex, isPro));
  }, [isPro]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [stats, setStats] = useState(() => {
    const initialDeck = createWeightedDeck(charactersWithIndex);
    return { known: 0, learning: 0, total: initialDeck.length };
  });
  const [streak, setStreak] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(null);
  const [showProfile, setShowProfile] = useState(false);

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true; // Default to dark mode
  });

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Theme colors
  const theme = {
    bg: isDarkMode
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
      : '#4FAEA5',
    cardBg: isDarkMode
      ? 'linear-gradient(145deg, #1e293b, #0f172a)'
      : 'linear-gradient(145deg, #ffffff, #f0f9f8)',
    cardBorder: isDarkMode
      ? 'rgba(255,255,255,0.1)'
      : 'rgba(0,0,0,0.1)',
    // Light mode uses white text for contrast against teal background
    text: isDarkMode ? '#f8fafc' : '#ffffff',
    textMuted: isDarkMode ? '#94a3b8' : 'rgba(255,255,255,0.9)',
    textSecondary: isDarkMode ? '#64748b' : 'rgba(255,255,255,0.7)',
    buttonBg: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
    buttonText: isDarkMode ? '#94a3b8' : '#ffffff',
  };

  const cardRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);

  // Refs to track latest auth state (avoids stale closure issues)
  const userRef = useRef(user);
  const guestModeRef = useRef(guestMode);

  // Keep refs in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    guestModeRef.current = guestMode;
  }, [guestMode]);

  const { playCorrect, playStreak, playWrong } = useSound();

  // Wrap handleSignOut to also reset local state
  const handleSignOut = async () => {
    await authSignOut();
    setStats({ known: 0, learning: 0, total: deck.length });
    setStreak(0);
    setKnownChars(new Set());
    setLearningChars(new Set());
  };

  // Save progress to database (uses refs to avoid stale closure on first swipe)
  const saveProgress = useCallback(async (charIndex, isKnown) => {
    const currentUser = userRef.current;
    const isGuestMode = guestModeRef.current;

    if (!currentUser || isGuestMode) return;

    try {
      await supabase.from('user_progress').upsert({
        user_id: currentUser.id,
        character_index: charIndex,
        known: isKnown,
        last_seen: new Date().toISOString()
      }, {
        onConflict: 'user_id,character_index'
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  }, []); // No dependencies needed - reads from refs

  const currentCard = deck[currentIndex];
  const progress = ((knownChars.size + learningChars.size) / deck.length) * 100;

  // Reshuffle deck and reset session stats (keeps known/learning lists)
  const handleReshuffle = useCallback(() => {
    const newDeck = createWeightedDeck(charactersWithIndex, isPro);
    setDeck(newDeck);
    setCurrentIndex(0);
    setStats({ known: 0, learning: 0, total: newDeck.length });
    setStreak(0);
    setIsFlipped(false);
  }, [isPro]);

  const handleSwipe = useCallback((direction) => {
    setSwipeDirection(direction);
    const charIndex = currentCard.originalIndex;

    // Save progress to database
    saveProgress(charIndex, direction === 'right');

    // Update character lists (move between known/learning)
    if (direction === 'right') {
      setKnownChars(prev => new Set([...prev, charIndex]));
      setLearningChars(prev => {
        const next = new Set(prev);
        next.delete(charIndex);
        return next;
      });
    } else {
      setLearningChars(prev => new Set([...prev, charIndex]));
      setKnownChars(prev => {
        const next = new Set(prev);
        next.delete(charIndex);
        return next;
      });
    }

    setTimeout(() => {
      if (direction === 'right') {
        setStats(s => ({ ...s, known: s.known + 1 }));
        const newStreak = streak + 1;
        setStreak(newStreak);

        // Check for milestone streaks
        if (newStreak === 10 || newStreak === 50 || newStreak === 100) {
          playStreak(newStreak);
          setShowStreakCelebration(newStreak);
        } else {
          playCorrect();
        }
      } else {
        setStats(s => ({ ...s, learning: s.learning + 1 }));
        setStreak(0); // Reset streak on wrong
        playWrong();
      }

      setSwipeDirection(null);
      setIsFlipped(false);
      setDragX(0);

      if (currentIndex < deck.length - 1) {
        setCurrentIndex(i => i + 1);
      } else {
        // Reshuffle and restart session (keeps character lists)
        handleReshuffle();
      }
    }, 300);
  }, [currentCard, currentIndex, deck.length, streak, playCorrect, playStreak, playWrong, handleReshuffle, saveProgress]);

  // Reset all progress (clears both lists and database)
  const handleResetAllProgress = async () => {
    if (!window.confirm('Are you sure you want to reset all progress? This cannot be undone.')) {
      return;
    }

    // Clear database first if logged in
    if (user && !guestMode) {
      // Use .select() to verify rows were actually deleted (RLS can silently fail)
      const { data, error } = await supabase
        .from('user_progress')
        .delete()
        .eq('user_id', user.id)
        .select();

      let deleteFailed = false;
      if (error) {
        deleteFailed = true;
        console.error('Error clearing progress:', error);
      } else {
        // Verify deletion by checking if any records still exist
        const { data: remaining, error: checkError } = await supabase
          .from('user_progress')
          .select('character_index')
          .eq('user_id', user.id)
          .limit(1);

        if (checkError) {
          console.error('Error verifying deletion:', checkError);
        } else if (remaining && remaining.length > 0) {
          console.error('Delete appeared to succeed but records still exist');
          deleteFailed = true;
        } else {
          console.log('Successfully deleted', data?.length || 0, 'progress records');
        }
      }

      if (deleteFailed) {
        alert('Progress cleared on this device, but the server reset failed. You may see old progress return after sync.');
      }
    }

    // Clear local state only after successful database deletion
    setKnownChars(new Set());
    setLearningChars(new Set());

    // Also reshuffle the deck
    handleReshuffle();
    setShowProfile(false);
  };

  const handlePointerDown = (e) => {
    setIsDragging(true);
    startX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    startY.current = e.clientY || e.touches?.[0]?.clientY || 0;
    startTime.current = Date.now();
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    if (isFlipped) return;
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const diff = currentX - startX.current;
    setDragX(diff);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) {
      return;
    }

    const endX = e.clientX || e.changedTouches?.[0]?.clientX || 0;
    const endY = e.clientY || e.changedTouches?.[0]?.clientY || 0;
    const diffX = endX - startX.current;
    const diffY = endY - startY.current;
    const elapsed = Date.now() - startTime.current;

    setIsDragging(false);

    // Check if it's a tap (small movement)
    if (Math.abs(diffX) < 22 && Math.abs(diffY) < 22 && elapsed < 500) {
      setIsFlipped(f => !f);
      setDragX(0);
      return;
    }

    // Check for swipe threshold
    if (!isFlipped && Math.abs(diffX) > 100) {
      handleSwipe(diffX > 0 ? 'right' : 'left');
    } else {
      setDragX(0);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (swipeDirection) return; // Ignore if already swiping

      if (e.key === 'ArrowRight') {
        handleSwipe('right');
      } else if (e.key === 'ArrowLeft') {
        handleSwipe('left');
      } else if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsFlipped(f => !f);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [swipeDirection, handleSwipe]);

  // Loading state
  if (authLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif',
      }}>
        <div style={{ color: '#94a3b8', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  // Auth screen
  if (!user && !guestMode) {
    return (
      <AuthForm
        authMode={authMode}
        setAuthMode={setAuthMode}
        authError={authError}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        handleSignIn={handleSignIn}
        handleSignUp={handleSignUp}
        handleSignInWithGoogle={handleSignInWithGoogle}
        continueAsGuest={continueAsGuest}
      />
    );
  }

  return (
    <div className="app-root" style={{
      background: theme.bg,
      transition: 'background 0.3s ease',
    }}>
      {/* Streak Celebration */}
      {showStreakCelebration && (
        <StreakCelebration
          milestone={showStreakCelebration}
          onComplete={() => setShowStreakCelebration(null)}
        />
      )}

      {/* Profile Modal */}
      {showProfile && (
        <div className="profile-modal-overlay" style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px',
        }}>
          <div className="profile-modal-content" style={{
            background: 'linear-gradient(135deg, #1e293b, #0f172a)',
            borderRadius: '20px',
            padding: 'clamp(16px, 4vw, 32px)',
            width: '100%',
            maxWidth: 'min(600px, calc(100vw - 32px))',
            maxHeight: '85vh',
            overflow: 'auto',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}>
              <h2 style={{ color: '#f8fafc', margin: 0, fontSize: '24px' }}>My Progress</h2>
              <button
                onClick={() => setShowProfile(false)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: '#94a3b8',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px',
                }}
              >
                ✕
              </button>
            </div>

            {/* Summary */}
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
            }}>
              <div style={{
                flex: 1,
                background: 'rgba(34,197,94,0.1)',
                border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
              }}>
                <div style={{ color: '#22c55e', fontSize: '32px', fontWeight: 'bold' }}>{knownChars.size}</div>
                <div style={{ color: '#86efac', fontSize: '14px' }}>Known</div>
              </div>
              <div style={{
                flex: 1,
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
              }}>
                <div style={{ color: '#ef4444', fontSize: '32px', fontWeight: 'bold' }}>{learningChars.size}</div>
                <div style={{ color: '#fca5a5', fontSize: '14px' }}>Learning</div>
              </div>
            </div>

            {/* Known Characters */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ color: '#22c55e', fontSize: '16px', marginBottom: '12px' }}>
                Known Characters ({knownChars.size})
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: '150px',
                overflow: 'auto',
                padding: '4px',
              }}>
                {knownChars.size === 0 ? (
                  <span style={{ color: '#64748b', fontSize: '14px' }}>No characters yet. Start swiping!</span>
                ) : (
                  [...knownChars].sort((a, b) => a - b).map(idx => (
                    <div
                      key={idx}
                      style={{
                        background: 'rgba(34,197,94,0.2)',
                        border: '1px solid rgba(34,197,94,0.4)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: '#f8fafc',
                        fontSize: '20px',
                        cursor: 'default',
                      }}
                      title={`${characters[idx].pinyin} - ${characters[idx].meaning}`}
                    >
                      {characters[idx].char}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Learning Characters */}
            <div>
              <h3 style={{ color: '#ef4444', fontSize: '16px', marginBottom: '12px' }}>
                Learning Characters ({learningChars.size})
              </h3>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: '150px',
                overflow: 'auto',
                padding: '4px',
              }}>
                {learningChars.size === 0 ? (
                  <span style={{ color: '#64748b', fontSize: '14px' }}>No characters in learning list.</span>
                ) : (
                  [...learningChars].sort((a, b) => a - b).map(idx => (
                    <div
                      key={idx}
                      style={{
                        background: 'rgba(239,68,68,0.2)',
                        border: '1px solid rgba(239,68,68,0.4)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        color: '#f8fafc',
                        fontSize: '20px',
                        cursor: 'default',
                      }}
                      title={`${characters[idx].pinyin} - ${characters[idx].meaning}`}
                    >
                      {characters[idx].char}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Reset Button */}
            {(knownChars.size > 0 || learningChars.size > 0) && (
              <div style={{
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
              }}>
                <button
                  onClick={handleResetAllProgress}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(239,68,68,0.5)',
                    background: 'rgba(239,68,68,0.1)',
                    color: '#ef4444',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Reset All Progress
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header styles */}
      <style>{`
        .app-root {
          height: 100vh;
          height: 100dvh;
          width: 100%;
          position: fixed;
          top: 0;
          left: 0;
          overflow: hidden;
          overscroll-behavior: none;
          touch-action: none;

          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;

          padding-top: calc(16px + env(safe-area-inset-top));
          padding-bottom: calc(16px + env(safe-area-inset-bottom));
          padding-left: 20px;
          padding-right: 20px;
          box-sizing: border-box;

          font-family: "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif;
        }

        /* Top section: header + progress */
        .top-section {
          width: 100%;
          max-width: 600px;
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
        }

        /* Middle section: card + buttons (takes available space) */
        .middle-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-height: 0;
          padding: 12px 0;
        }

        /* Bottom section: instructions + pro button */
        .bottom-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex-shrink: 0;
          width: 100%;
        }

        .app-header {
          position: relative;
          width: 100%;
          max-width: 600px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          z-index: 10;
          margin-bottom: 12px;
        }
        .progress-bar-container {
          position: relative;
          width: 100%;
          max-width: 600px;
          z-index: 9;
        }
        .header-stats {
          font-size: 14px;
          white-space: nowrap;
        }
        .header-buttons {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .header-btn {
          padding: 6px 12px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 12px;
          white-space: nowrap;
        }
        .header-btn-text {
          display: inline;
        }
        .header-btn-icon {
          display: none;
        }
        .card-stack {
          position: relative;
          width: min(320px, 85vw);
          height: min(440px, 50vh);
          perspective: 1000px;
          z-index: 5;
          flex-shrink: 0;
        }
        .action-buttons {
          display: flex;
          gap: 48px;
          margin-top: clamp(16px, 3vh, 28px);
          z-index: 10;
          flex-shrink: 0;
        }
        .action-btn {
          width: 72px;
          height: 72px;
          font-size: 32px;
        }
        .card-char-front {
          font-size: clamp(100px, 22vw, 160px);
        }
        .card-char-back {
          font-size: clamp(48px, 12vw, 64px);
        }
        .card-pinyin {
          font-size: clamp(22px, 5vw, 28px);
        }
        .card-meaning {
          font-size: clamp(14px, 3.5vw, 16px);
        }
        .card-example {
          font-size: clamp(11px, 3vw, 13px);
        }
        .pro-unlock-btn {
          margin-top: clamp(8px, 2vh, 16px);
        }
        .instructions-text {
          font-size: 13px;
          margin-bottom: 4px;
        }

        /* Mobile devices (under 600px width) */
        @media (max-width: 600px) {
          .app-root {
            padding-left: 16px;
            padding-right: 16px;
          }
          .app-header {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            padding: 10px 12px;
            border-radius: 16px;
            background: rgba(15, 23, 42, 0.55);
            border: 1px solid rgba(255, 255, 255, 0.12);
            box-shadow: 0 8px 24px rgba(0,0,0,0.3);
            backdrop-filter: blur(12px);
            margin-bottom: 12px;
          }
          .header-stats {
            font-size: 13px;
            text-align: center;
          }
          .header-buttons {
            justify-content: center;
            gap: 6px;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .header-buttons::-webkit-scrollbar {
            display: none;
          }
          .header-btn {
            padding: 6px 10px;
            font-size: 11px;
            border: 1px solid rgba(255,255,255,0.12);
            background-clip: padding-box;
          }
          .header-btn-text {
            display: none;
          }
          .header-btn-icon {
            display: inline;
          }
          .card-stack {
            width: min(320px, 85vw);
            height: min(440px, 48vh);
          }
          .action-buttons {
            gap: 40px;
          }
          .action-btn {
            width: 68px;
            height: 68px;
            font-size: 30px;
          }
          .instructions-text {
            font-size: 12px;
          }
        }

        /* Very small phones (iPhone SE, small Android) */
        @media (max-width: 375px) {
          .app-root {
            padding-left: 12px;
            padding-right: 12px;
            padding-top: calc(12px + env(safe-area-inset-top));
          }
          .app-header {
            padding: 8px 10px;
            gap: 6px;
            margin-bottom: 10px;
            border-radius: 14px;
          }
          .header-stats {
            font-size: 12px;
          }
          .header-btn {
            padding: 5px 8px;
            font-size: 10px;
          }
          .card-stack {
            width: min(290px, 88vw);
            height: min(380px, 46vh);
          }
          .action-buttons {
            gap: 32px;
          }
          .action-btn {
            width: 60px;
            height: 60px;
            font-size: 26px;
          }
          .instructions-text {
            font-size: 11px;
          }
        }

        /* Tall phones (iPhone Pro Max, large Android) - optimize vertical space */
        @media (min-height: 800px) and (max-width: 600px) {
          .card-stack {
            width: min(340px, 88vw);
            height: min(480px, 52vh);
          }
          .action-btn {
            width: 72px;
            height: 72px;
            font-size: 32px;
          }
          .action-buttons {
            gap: 48px;
            margin-top: 24px;
          }
          .middle-section {
            padding: 20px 0;
          }
        }

        /* Very tall phones (iPhone Pro Max) */
        @media (min-height: 900px) and (max-width: 500px) {
          .card-stack {
            width: min(350px, 90vw);
            height: min(520px, 54vh);
          }
          .action-buttons {
            margin-top: 28px;
          }
        }

        /* Short screens (landscape or small phones) */
        @media (max-height: 700px) {
          .app-root {
            padding-top: calc(10px + env(safe-area-inset-top));
            padding-bottom: calc(10px + env(safe-area-inset-bottom));
          }
          .card-stack {
            height: min(360px, 46vh);
          }
          .action-btn {
            width: 60px;
            height: 60px;
            font-size: 26px;
          }
          .action-buttons {
            gap: 32px;
          }
          .middle-section {
            padding: 8px 0;
          }
        }

        /* Very short screens */
        @media (max-height: 600px) {
          .app-root {
            padding-top: calc(8px + env(safe-area-inset-top));
            padding-bottom: calc(8px + env(safe-area-inset-bottom));
          }
          .app-header {
            margin-bottom: 8px;
            padding: 6px 10px;
          }
          .card-stack {
            height: min(300px, 44vh);
          }
          .action-btn {
            width: 54px;
            height: 54px;
            font-size: 24px;
          }
          .action-buttons {
            gap: 28px;
          }
          .instructions-text {
            font-size: 10px;
          }
          .middle-section {
            padding: 4px 0;
          }
        }

        /* Ultra-short screens (extreme cases) */
        @media (max-height: 500px) {
          .card-stack {
            height: min(240px, 42vh);
          }
          .action-btn {
            width: 48px;
            height: 48px;
            font-size: 20px;
          }
          .instructions-text {
            display: none;
          }
        }

        /* Combined: very small AND short */
        @media (max-width: 375px) and (max-height: 650px) {
          .card-stack {
            width: min(260px, 85vw);
            height: min(280px, 44vh);
          }
        }
      `}</style>

      {/* TOP SECTION: Header + Progress */}
      <div className="top-section">
        <div className="app-header">
          <div className="header-stats" style={{ color: theme.textMuted }}>
            <span style={{ color: '#22c55e', fontWeight: 'bold' }}>{knownChars.size}</span> 记住 ·
            <span style={{ color: '#ef4444', fontWeight: 'bold', marginLeft: '8px' }}>{learningChars.size}</span> 学习中
            {guestMode && <span style={{ marginLeft: '8px', color: theme.textSecondary }}>(Guest)</span>}
          </div>
          <div className="header-buttons">
            {/* User info & logout */}
            {user && (
              <button
                onClick={handleSignOut}
                className="header-btn"
                style={{ background: theme.buttonBg, color: theme.buttonText }}
              >
                <span className="header-btn-text">Sign out</span>
                <span className="header-btn-icon">👋</span>
              </button>
            )}
            {guestMode && (
              <button
                onClick={() => setGuestMode(false)}
                className="header-btn"
                style={{ background: theme.buttonBg, color: theme.buttonText }}
              >
                <span className="header-btn-text">Sign in</span>
                <span className="header-btn-icon">🔑</span>
              </button>
            )}
            {/* Profile button */}
            <button
              onClick={() => setShowProfile(true)}
              className="header-btn"
              style={{ background: theme.buttonBg, color: theme.buttonText }}
            >
              <span className="header-btn-text">Progress ({knownChars.size + learningChars.size}/{deck.length})</span>
              <span className="header-btn-icon">📊 {knownChars.size + learningChars.size}</span>
            </button>
            {/* Reshuffle button */}
            <button
              onClick={handleReshuffle}
              className="header-btn"
              style={{ background: theme.buttonBg, color: theme.buttonText }}
            >
              <span className="header-btn-text">Reshuffle</span>
              <span className="header-btn-icon">🔀</span>
            </button>
            {/* Theme toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="header-btn"
              style={{ background: theme.buttonBg, color: theme.buttonText }}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            {/* Streak indicator */}
            {streak > 0 && (
              <div className="header-btn header-streak" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: streak >= 10
                  ? 'linear-gradient(135deg, #f59e0b, #ef4444)'
                  : 'rgba(251, 191, 36, 0.2)',
                animation: streak >= 5 ? 'pulse 1s ease-in-out infinite' : 'none',
              }}>
                <span style={{ fontSize: '12px' }}>🔥</span>
                <span style={{
                  color: streak >= 10 ? 'white' : '#fbbf24',
                  fontWeight: 'bold',
                  fontSize: '12px',
                }}>
                  {streak}
                </span>
              </div>
            )}
            <div className="header-btn header-count" style={{ background: theme.buttonBg, color: theme.textSecondary }}>
              {currentIndex + 1}/{deck.length}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>

        {/* Progress bar */}
        <div className="progress-bar-container" style={{
          height: '4px',
          background: '#1e293b',
          borderRadius: '2px',
          overflow: 'hidden',
          zIndex: 9,
          pointerEvents: 'none',
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #22c55e, #3b82f6)',
            borderRadius: '2px',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* MIDDLE SECTION: Card + Action Buttons */}
      <div className="middle-section">

      {/* Card Stack Visual */}
      <div className="card-stack">
        {/* Background cards for stack effect */}
        {[2, 1].map((offset) => (
          <div key={offset} style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: isDarkMode
              ? 'linear-gradient(145deg, #1e293b, #334155)'
              : 'linear-gradient(145deg, #d1d5db, #e5e7eb)',
            borderRadius: '24px',
            transform: `translateY(${offset * 8}px) scale(${1 - offset * 0.05})`,
            opacity: 0.5 - offset * 0.15,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} />
        ))}

        {/* Main Card */}
        <FlashCard
          ref={cardRef}
          currentCard={currentCard}
          currentIndex={currentIndex}
          deckLength={deck.length}
          isFlipped={isFlipped}
          isDragging={isDragging}
          dragX={dragX}
          swipeDirection={swipeDirection}
          isDarkMode={isDarkMode}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={() => { if (isDragging) { setIsDragging(false); setDragX(0); } }}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        />
      </div>

      {/* Action buttons */}
      <div className="action-buttons">
        <button
          className="action-btn"
          onClick={() => handleSwipe('left')}
          style={{
            borderRadius: '50%',
            border: '3px solid #ef4444',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 20px rgba(239,68,68,0.3)',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#ef4444';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        >
          ✕
        </button>

        <button
          className="action-btn"
          onClick={() => handleSwipe('right')}
          style={{
            borderRadius: '50%',
            border: '3px solid #22c55e',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#22c55e';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        >
          ✓
        </button>
      </div>
      </div>

      {/* BOTTOM SECTION: Instructions + Pro Button */}
      <div className="bottom-section">
        {/* Instructions */}
        <div className="instructions-text" style={{
          color: theme.textSecondary,
          textAlign: 'center',
          opacity: 0.7,
        }}>
          ← 再学习 &nbsp;·&nbsp; 点击翻转 &nbsp;·&nbsp; 记住了 →
        </div>
        {/* Pro Badge / Unlock Button */}
        {!isPro && (
          <button
            className="pro-unlock-btn"
            onClick={() => setShowPaywall(true)}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #ffd700, #f59e0b)',
              borderRadius: '24px',
              border: 'none',
              color: '#713f12',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: 'pointer',
              boxShadow: '0 6px 16px rgba(245, 158, 11, 0.4)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              animation: 'pulse 2s infinite',
            }}
          >
            <span>👑</span> Unlock Pro
          </button>
        )}
      </div>

      {/* Free Limit Modal */}
      {showFreeLimitModal && (
        <FreeLimitModal
          onClose={() => setShowFreeLimitModal(false)}
          onUpgrade={() => {
            setShowFreeLimitModal(false);
            setShowPaywall(true);
          }}
        />
      )}

      {/* Paywall Modal */}
      {showPaywall && <Paywall onClose={() => setShowPaywall(false)} />}
    </div>
  );
};

export default function ChineseFlashcards() {
  return (
    <SubscriptionProvider>
      <AppContent />
    </SubscriptionProvider>
  );
}
